import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { getOutlets, checkMonthlyReading } from '../api';

function OutletSelection() {
  const [outlets, setOutlets] = useState([]);
  const [filteredOutlets, setFilteredOutlets] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [monthlyReadings, setMonthlyReadings] = useState({});
  const [loading, setLoading] = useState(true);
  const { utilityId } = useParams();
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchOutlets();
  }, [utilityId]);

  useEffect(() => {
    filterOutlets();
  }, [searchQuery, outlets]);

  const fetchOutlets = async () => {
    try {
      const response = await getOutlets(utilityId);
      setOutlets(response.data);
      setFilteredOutlets(response.data);
      
      // Check monthly readings for each outlet
      const readingChecks = {};
      await Promise.all(
        response.data.map(async (outlet) => {
          try {
            const checkRes = await checkMonthlyReading(outlet.id);
            readingChecks[outlet.id] = checkRes.data.hasReading;
          } catch (error) {
            console.error(`Error checking monthly reading for outlet ${outlet.id}:`, error);
            readingChecks[outlet.id] = false;
          }
        })
      );
      setMonthlyReadings(readingChecks);
    } catch (error) {
      console.error('Error fetching outlets:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterOutlets = () => {
    if (!searchQuery.trim()) {
      setFilteredOutlets(outlets);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = outlets.filter(outlet =>
      outlet.tenantName.toLowerCase().includes(query) ||
      outlet.location.toLowerCase().includes(query) ||
      outlet.meterNo?.toLowerCase().includes(query) ||
      outlet.serialNo?.toLowerCase().includes(query)
    );
    setFilteredOutlets(filtered);
  };

  const handleOutletClick = (outletId) => {
    navigate(`/dashboard/reading/${outletId}`);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return <div className="loading">Loading outlets...</div>;
  }

  const utilityName = outlets[0]?.Utility?.name || 'Utility';

  return (
    <div>
      <div className="navbar">
        <h1>MeterBill Pro</h1>
        <div className="navbar-right">
          <span className="navbar-user">{user?.username}</span>
          <button onClick={handleLogout}>Logout</button>
        </div>
      </div>
      <div className="container">
        <button onClick={() => navigate('/dashboard')} className="btn btn-secondary" style={{ marginBottom: '24px' }}>
          ← Back to Utilities
        </button>

        <div className="page-header">
          <h2>{utilityName}</h2>
          <p>Select an outlet to submit meter reading</p>
        </div>

        <div className="card">
          <div className="search-box">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search by tenant, location, meter number, or serial number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {filteredOutlets.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📭</div>
              <h3>No outlets found</h3>
              <p>{searchQuery ? 'Try adjusting your search query' : 'No outlets available for this utility'}</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Tenant Name</th>
                    <th>Location</th>
                    <th>Meter No</th>
                    <th>Serial No</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOutlets.map((outlet) => (
                    <tr key={outlet.id}>
                      <td>
                        {!outlet.active ? (
                          <span className="badge" style={{ background: '#6b7280', color: 'white' }}>
                            ⊗ Disabled
                          </span>
                        ) : monthlyReadings[outlet.id] ? (
                          <span className="badge" style={{ background: '#dc2626', color: 'white' }}>
                            ✓ Completed
                          </span>
                        ) : (
                          <span className="badge" style={{ background: '#f59e0b', color: 'white' }}>
                            Pending
                          </span>
                        )}
                      </td>
                      <td><strong>{outlet.tenantName}</strong></td>
                      <td>{outlet.location}</td>
                      <td>{outlet.meterNo || '-'}</td>
                      <td><span className="badge badge-primary">{outlet.serialNo}</span></td>
                      <td>
                        <button
                          onClick={() => handleOutletClick(outlet.id)}
                          className="btn btn-primary"
                          disabled={!outlet.active || monthlyReadings[outlet.id]}
                          style={{
                            opacity: (outlet.active && !monthlyReadings[outlet.id]) ? 1 : 0.5,
                            cursor: (outlet.active && !monthlyReadings[outlet.id]) ? 'pointer' : 'not-allowed'
                          }}
                        >
                          {!outlet.active ? 'Disabled' : monthlyReadings[outlet.id] ? '✓ Already Submitted' : 'Submit Reading →'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default OutletSelection;
